import { useState, useMemo } from 'react';
import { Briefcase, Plus, Upload, Wifi, Search, ArrowUpDown, TrendingUp, TrendingDown, MoreHorizontal, Trash2, Edit, Receipt, AlertTriangle, X, Crown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { usePortfolioHoldings, PortfolioHolding } from '@/hooks/usePortfolioHoldings';
import { usePortfolioTransactions } from '@/hooks/usePortfolioTransactions';
import { usePortfolioSummary } from '@/hooks/usePortfolioSummary';
import { useSubscription } from '@/hooks/useSubscription';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { AddHoldingModal } from '@/components/portfolio/AddHoldingModal';
import { CSVImportModal } from '@/components/portfolio/CSVImportModal';
import { AddTransactionModal } from '@/components/portfolio/AddTransactionModal';
import { PortfolioInsights } from '@/components/portfolio/PortfolioInsights';
import { PortfolioNewsWidget } from '@/components/portfolio/PortfolioNewsWidget';
import { PerformanceChart } from '@/components/portfolio/PerformanceChart';
import { BenchmarkComparison } from '@/components/portfolio/BenchmarkComparison';

const ASSET_COLORS = [
  'hsl(210, 100%, 56%)',  // blue - stocks
  'hsl(38, 92%, 50%)',    // amber - crypto
  'hsl(142, 71%, 45%)',   // green - forex
  'hsl(265, 90%, 65%)',   // purple - commodities
  'hsl(0, 84%, 60%)',     // red - ETF
  'hsl(180, 70%, 45%)',   // teal - bonds
];

const SECTOR_COLORS = [
  'hsl(210, 100%, 56%)', 'hsl(142, 71%, 45%)', 'hsl(38, 92%, 50%)',
  'hsl(265, 90%, 65%)', 'hsl(0, 84%, 60%)', 'hsl(180, 70%, 45%)',
  'hsl(320, 80%, 55%)', 'hsl(160, 60%, 50%)', 'hsl(45, 100%, 55%)',
  'hsl(200, 80%, 50%)',
];

type SortKey = 'symbol' | 'quantity' | 'average_price' | 'current_price' | 'market_value' | 'unrealized_pnl' | 'unrealized_pnl_pct' | 'sector';

const fmt = (n: number, decimals = 2) => n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
const fmtUsd = (n: number) => `$${fmt(n)}`;

const PortfolioPage = () => {
  const { user } = useAuth();
  const { holdings, isLoading, isDemoPortfolio, totalValue, totalPnl, totalPnlPct, sectorBreakdown, deleteHolding } = usePortfolioHoldings();
  const { transactions } = usePortfolioTransactions();
  const { summary } = usePortfolioSummary();
  const { tier } = useSubscription();

  const [showAddHolding, setShowAddHolding] = useState(false);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [showAddTx, setShowAddTx] = useState(false);
  const [txSymbol, setTxSymbol] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('market_value');
  const [sortAsc, setSortAsc] = useState(false);
  const [demoBannerDismissed, setDemoBannerDismissed] = useState(false);

  const { data: brokerCount = 0 } = useQuery({
    queryKey: ['broker-connections-count', user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { count } = await supabase.from('broker_connections').select('*', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'connected');
      return count ?? 0;
    },
    enabled: !!user,
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const filteredHoldings = useMemo(() => {
    let list = [...holdings];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(h => h.symbol.toLowerCase().includes(q) || (h.asset_name || '').toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      if (typeof av === 'string' && typeof bv === 'string') return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortAsc ? (Number(av) - Number(bv)) : (Number(bv) - Number(av));
    });
    return list;
  }, [holdings, searchQuery, sortKey, sortAsc]);

  // Asset allocation
  const assetAllocation = useMemo(() => {
    const map: Record<string, number> = {};
    holdings.forEach(h => { map[h.asset_type] = (map[h.asset_type] || 0) + (h.market_value || 0); });
    return Object.entries(map).map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }));
  }, [holdings]);

  // Sector allocation (stocks only)
  const sectorAllocation = useMemo(() => {
    const map: Record<string, number> = {};
    holdings.filter(h => h.asset_type === 'stock').forEach(h => {
      map[h.sector || 'Other'] = (map[h.sector || 'Other'] || 0) + (h.market_value || 0);
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [holdings]);

  // Top movers
  const topGainers = useMemo(() =>
    [...holdings].filter(h => h.unrealized_pnl_pct != null).sort((a, b) => (b.unrealized_pnl_pct || 0) - (a.unrealized_pnl_pct || 0)).slice(0, 3),
  [holdings]);
  const topLosers = useMemo(() =>
    [...holdings].filter(h => h.unrealized_pnl_pct != null).sort((a, b) => (a.unrealized_pnl_pct || 0) - (b.unrealized_pnl_pct || 0)).slice(0, 3),
  [holdings]);

  // Health stats
  const healthStats = useMemo(() => {
    const uniqueSectors = new Set(holdings.filter(h => h.asset_type === 'stock').map(h => h.sector || 'Other'));
    const diversification = Math.min(100, Math.round((uniqueSectors.size / 11) * 100));
    const maxHolding = Math.max(...holdings.map(h => h.market_value || 0), 0);
    const concentration = totalValue > 0 ? (maxHolding / totalValue) * 100 : 0;
    const cryptoVal = holdings.filter(h => h.asset_type === 'crypto').reduce((s, h) => s + (h.market_value || 0), 0);
    const forexVal = holdings.filter(h => h.asset_type === 'forex').reduce((s, h) => s + (h.market_value || 0), 0);
    return {
      diversification,
      concentration: totalValue > 0 ? concentration : 0,
      cryptoExposure: totalValue > 0 ? (cryptoVal / totalValue) * 100 : 0,
      forexExposure: totalValue > 0 ? (forexVal / totalValue) * 100 : 0,
    };
  }, [holdings, totalValue]);

  const dailyPnl = summary?.daily_pnl ?? 0;
  const dailyPnlPct = summary?.daily_pnl_pct ?? 0;

  const recentTx = (transactions || []).slice(0, 10);

  const SortHeader = ({ label, sortKeyName }: { label: string; sortKeyName: SortKey }) => (
    <TableHead className="cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => handleSort(sortKeyName)}>
      <span className="flex items-center gap-1">
        {label}
        {sortKey === sortKeyName && <ArrowUpDown className="w-3 h-3" />}
      </span>
    </TableHead>
  );

  if (isLoading) {
    return (
      <div className="p-4 lg:p-6 space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-primary" />
          <h1 className="text-lg font-bold font-heading">Portfolio Intelligence</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setShowAddHolding(true)} className="gap-1.5 h-8 text-xs">
            <Plus className="w-3.5 h-3.5" /> Add Holding
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowCSVImport(true)} className="gap-1.5 h-8 text-xs">
            <Upload className="w-3.5 h-3.5" /> Import CSV
          </Button>
          <Button size="sm" variant="outline" onClick={() => {/* navigate to brokers handled by parent */}} className="gap-1.5 h-8 text-xs">
            <Wifi className="w-3.5 h-3.5" /> Connect Broker
          </Button>
        </div>
      </div>

      {/* Demo banner */}
      {isDemoPortfolio && !demoBannerDismissed && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-[hsl(var(--accent-blue)/0.1)] border border-[hsl(var(--accent-blue)/0.2)] text-xs">
          <span className="text-[hsl(var(--accent-blue))]">📊 Demo portfolio loaded with 10 sample holdings. Add your own or connect a broker.</span>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setDemoBannerDismissed(true)}><X className="w-3 h-3" /></Button>
        </div>
      )}

      {/* Broker CTA */}
      {brokerCount === 0 && !isDemoPortfolio && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[hsl(var(--accent-amber)/0.08)] border border-[hsl(var(--accent-amber)/0.2)]">
          <AlertTriangle className="w-4 h-4 text-[hsl(var(--accent-amber))] flex-shrink-0" />
          <div className="flex-1 text-xs">
            <span className="font-medium text-foreground">Connect your broker for auto-sync.</span>
            <span className="text-muted-foreground ml-1">Supported: Zerodha, Dhan, Upstox, AngelOne, Binance, Alpaca</span>
          </div>
        </div>
      )}

      {/* Free-tier banner */}
      {tier === 'free' && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-[hsl(var(--accent-amber)/0.06)] border border-[hsl(var(--accent-amber)/0.15)] text-xs">
          <span className="text-muted-foreground flex items-center gap-1.5">
            <Crown className="w-3.5 h-3.5 text-[hsl(var(--accent-amber))]" />
            Free Plan: Read-only portfolio analysis. Upgrade to Pro for automated trading.
          </span>
          <Button variant="link" size="sm" className="h-auto p-0 text-[10px] text-[hsl(var(--accent-amber))]">Upgrade</Button>
        </div>
      )}

      {/* Summary Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-border/30">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Total Value</p>
            <p className="text-lg font-mono font-bold tabular-nums">{fmtUsd(totalValue)}</p>
          </CardContent>
        </Card>
        <Card className="border-border/30">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Total P&L</p>
            <p className={`text-lg font-mono font-bold tabular-nums ${totalPnl >= 0 ? 'text-[hsl(var(--accent-green))]' : 'text-[hsl(var(--accent-red))]'}`}>
              {totalPnl >= 0 ? '+' : ''}{fmtUsd(totalPnl)} <span className="text-xs">({totalPnlPct >= 0 ? '+' : ''}{fmt(totalPnlPct, 1)}%)</span>
            </p>
          </CardContent>
        </Card>
        <Card className="border-border/30">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Today</p>
            <p className={`text-lg font-mono font-bold tabular-nums ${dailyPnl >= 0 ? 'text-[hsl(var(--accent-green))]' : 'text-[hsl(var(--accent-red))]'}`}>
              {dailyPnl >= 0 ? '+' : ''}{fmtUsd(dailyPnl)} <span className="text-xs">({dailyPnlPct >= 0 ? '+' : ''}{fmt(dailyPnlPct, 1)}%)</span>
            </p>
          </CardContent>
        </Card>
        <Card className="border-border/30">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Holdings</p>
            <p className="text-lg font-mono font-bold tabular-nums">{holdings.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* LEFT — 60% */}
        <div className="lg:col-span-3 space-y-4">
          {/* Holdings Table */}
          <Card className="border-border/30">
            <CardHeader className="pb-2 px-4 pt-4">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-sm font-heading">Holdings</CardTitle>
                <div className="relative w-48">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="h-7 pl-7 text-xs" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-2">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="text-[10px] uppercase tracking-wider">
                      <SortHeader label="Symbol" sortKeyName="symbol" />
                      <TableHead className="hidden lg:table-cell">Name</TableHead>
                      <SortHeader label="Qty" sortKeyName="quantity" />
                      <SortHeader label="Avg" sortKeyName="average_price" />
                      <SortHeader label="Price" sortKeyName="current_price" />
                      <SortHeader label="Value" sortKeyName="market_value" />
                      <SortHeader label="P&L" sortKeyName="unrealized_pnl" />
                      <SortHeader label="P&L %" sortKeyName="unrealized_pnl_pct" />
                      <TableHead className="hidden lg:table-cell">Sector</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredHoldings.map((h) => {
                      const pnl = h.unrealized_pnl ?? 0;
                      const pnlPct = h.unrealized_pnl_pct ?? 0;
                      const pnlColor = pnl >= 0 ? 'text-[hsl(var(--accent-green))]' : 'text-[hsl(var(--accent-red))]';
                      return (
                        <TableRow key={h.id} className="text-xs">
                          <TableCell className="font-mono font-semibold sticky left-0 bg-card z-10">{h.symbol}</TableCell>
                          <TableCell className="hidden lg:table-cell text-muted-foreground truncate max-w-[120px]">{h.asset_name || '—'}</TableCell>
                          <TableCell className="font-mono tabular-nums">{fmt(h.quantity, h.quantity < 1 ? 4 : 0)}</TableCell>
                          <TableCell className="font-mono tabular-nums">{fmtUsd(h.average_price)}</TableCell>
                          <TableCell className="font-mono tabular-nums">{h.current_price ? fmtUsd(h.current_price) : '—'}</TableCell>
                          <TableCell className="font-mono tabular-nums">{h.market_value ? fmtUsd(h.market_value) : '—'}</TableCell>
                          <TableCell className={`font-mono tabular-nums ${pnlColor}`}>{pnl >= 0 ? '+' : ''}{fmtUsd(pnl)}</TableCell>
                          <TableCell className={`font-mono tabular-nums ${pnlColor}`}>{pnlPct >= 0 ? '+' : ''}{fmt(pnlPct, 1)}%</TableCell>
                          <TableCell className="hidden lg:table-cell text-muted-foreground">{h.sector || '—'}</TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0"><MoreHorizontal className="w-3.5 h-3.5" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-36">
                                <DropdownMenuItem onClick={() => { setTxSymbol(h.symbol); setShowAddTx(true); }}>
                                  <Receipt className="w-3.5 h-3.5 mr-2" /> Add Transaction
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => deleteHolding.mutate(h.id)} className="text-destructive">
                                  <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredHoldings.length === 0 && (
                      <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">No holdings found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <Card className="border-border/30">
            <CardHeader className="pb-2 px-4 pt-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-heading">Recent Transactions</CardTitle>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => { setTxSymbol(''); setShowAddTx(true); }}>
                  <Receipt className="w-3 h-3" /> Log Transaction
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-2">
              <Table>
                <TableHeader>
                  <TableRow className="text-[10px] uppercase tracking-wider">
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentTx.map(tx => {
                    const typeBg = tx.transaction_type === 'buy' ? 'bg-[hsl(var(--accent-green)/0.15)] text-[hsl(var(--accent-green))]'
                      : tx.transaction_type === 'sell' ? 'bg-[hsl(var(--accent-red)/0.15)] text-[hsl(var(--accent-red))]'
                      : 'bg-[hsl(var(--accent-amber)/0.15)] text-[hsl(var(--accent-amber))]';
                    return (
                      <TableRow key={tx.id} className="text-xs">
                        <TableCell className="font-mono tabular-nums text-muted-foreground">{new Date(tx.executed_at).toLocaleDateString()}</TableCell>
                        <TableCell><Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${typeBg} border-0`}>{tx.transaction_type.toUpperCase()}</Badge></TableCell>
                        <TableCell className="font-mono font-semibold">{tx.symbol}</TableCell>
                        <TableCell className="font-mono tabular-nums">{fmt(tx.quantity, tx.quantity < 1 ? 4 : 0)}</TableCell>
                        <TableCell className="font-mono tabular-nums">{fmtUsd(tx.price)}</TableCell>
                        <TableCell className="font-mono tabular-nums">{fmtUsd(tx.total_value || tx.quantity * tx.price)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {recentTx.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No transactions yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT — 40% */}
        <div className="lg:col-span-2 space-y-4">
          {/* Asset Allocation Donut */}
          <Card className="border-border/30">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardTitle className="text-sm font-heading">Asset Allocation</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {assetAllocation.length > 0 ? (
                <div className="flex flex-col items-center">
                  <div className="w-full h-[180px] relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={assetAllocation} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={2} strokeWidth={0}>
                          {assetAllocation.map((_, i) => <Cell key={i} fill={ASSET_COLORS[i % ASSET_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v: number) => fmtUsd(v)} contentStyle={{ background: 'hsl(220 15% 10%)', border: '1px solid hsl(220 15% 20%)', borderRadius: 8, fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="text-center">
                        <p className="text-[10px] text-muted-foreground">Total</p>
                        <p className="text-sm font-mono font-bold tabular-nums">{fmtUsd(totalValue)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
                    {assetAllocation.map((item, i) => (
                      <div key={item.name} className="flex items-center gap-1.5 text-[10px]">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ASSET_COLORS[i % ASSET_COLORS.length] }} />
                        <span className="text-muted-foreground">{item.name}</span>
                        <span className="font-mono tabular-nums">{totalValue > 0 ? fmt((item.value / totalValue) * 100, 0) : 0}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-center text-muted-foreground text-xs py-8">No allocation data</p>
              )}
            </CardContent>
          </Card>

          {/* Sector Allocation Donut */}
          {sectorAllocation.length > 0 && (
            <Card className="border-border/30">
              <CardHeader className="pb-1 px-4 pt-4">
                <CardTitle className="text-sm font-heading">Sector Allocation (Stocks)</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="flex flex-col items-center">
                  <div className="w-full h-[180px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={sectorAllocation} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={2} strokeWidth={0}>
                          {sectorAllocation.map((_, i) => <Cell key={i} fill={SECTOR_COLORS[i % SECTOR_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v: number) => fmtUsd(v)} contentStyle={{ background: 'hsl(220 15% 10%)', border: '1px solid hsl(220 15% 20%)', borderRadius: 8, fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
                    {sectorAllocation.map((item, i) => {
                      const total = sectorAllocation.reduce((s, x) => s + x.value, 0);
                      return (
                        <div key={item.name} className="flex items-center gap-1.5 text-[10px]">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: SECTOR_COLORS[i % SECTOR_COLORS.length] }} />
                          <span className="text-muted-foreground">{item.name}</span>
                          <span className="font-mono tabular-nums">{total > 0 ? fmt((item.value / total) * 100, 0) : 0}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Movers */}
          <Card className="border-border/30">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-sm font-heading">Top Movers</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              {topGainers.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Gainers</p>
                  {topGainers.map(h => (
                    <div key={h.id} className="flex items-center justify-between py-1.5">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-3.5 h-3.5 text-[hsl(var(--accent-green))]" />
                        <span className="text-xs font-mono font-semibold">{h.symbol}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-mono tabular-nums text-[hsl(var(--accent-green))]">+{fmt(h.unrealized_pnl_pct || 0, 1)}%</span>
                        <span className="text-[10px] text-muted-foreground ml-2 font-mono tabular-nums">{h.current_price ? fmtUsd(h.current_price) : '—'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {topLosers.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Losers</p>
                  {topLosers.map(h => (
                    <div key={h.id} className="flex items-center justify-between py-1.5">
                      <div className="flex items-center gap-2">
                        <TrendingDown className="w-3.5 h-3.5 text-[hsl(var(--accent-red))]" />
                        <span className="text-xs font-mono font-semibold">{h.symbol}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-mono tabular-nums text-[hsl(var(--accent-red))]">{fmt(h.unrealized_pnl_pct || 0, 1)}%</span>
                        <span className="text-[10px] text-muted-foreground ml-2 font-mono tabular-nums">{h.current_price ? fmtUsd(h.current_price) : '—'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {topGainers.length === 0 && topLosers.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">No P&L data available</p>
              )}
            </CardContent>
          </Card>

          {/* Health Quick Stats */}
          <Card className="border-border/30">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-sm font-heading">Portfolio Health</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              {[
                { label: 'Diversification Score', value: `${healthStats.diversification}%`, color: healthStats.diversification > 50 ? 'text-[hsl(var(--accent-green))]' : 'text-[hsl(var(--accent-amber))]' },
                { label: 'Concentration Risk', value: `${fmt(healthStats.concentration, 1)}%`, color: healthStats.concentration > 40 ? 'text-[hsl(var(--accent-red))]' : 'text-[hsl(var(--accent-green))]' },
                { label: 'Crypto Exposure', value: `${fmt(healthStats.cryptoExposure, 1)}%`, color: 'text-foreground' },
                { label: 'Forex Exposure', value: `${fmt(healthStats.forexExposure, 1)}%`, color: 'text-foreground' },
              ].map(stat => (
                <div key={stat.label} className="flex items-center justify-between py-1">
                  <span className="text-xs text-muted-foreground">{stat.label}</span>
                  <span className={`text-xs font-mono font-semibold tabular-nums ${stat.color}`}>{stat.value}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Portfolio Insights */}
          <PortfolioInsights holdings={holdings} totalValue={totalValue} />

          {/* Performance Chart */}
          <PerformanceChart />

          {/* Benchmark Comparison */}
          <BenchmarkComparison portfolioReturn={totalPnlPct} />

          {/* Portfolio News — live financial news for top holdings */}
          <PortfolioNewsWidget holdings={holdings} />
        </div>
      </div>

      {/* Modals */}
      <AddHoldingModal open={showAddHolding} onOpenChange={setShowAddHolding} />
      <CSVImportModal open={showCSVImport} onOpenChange={setShowCSVImport} />
      <AddTransactionModal open={showAddTx} onOpenChange={setShowAddTx} />
    </div>
  );
};

export default PortfolioPage;
